import { Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Sensor, SensorGroup, SensorReadingCriteria, SensorReadingCriteria2, SensorReadingType, TimeZoneDisplay as TimeZoneSelection, UserGroup } from '../../shared/models';
import { DateRanges } from '@models/ranges';
import { decodeDateRange, encodeDateRange as encodeDateRangeValue } from 'app/shared/classes/dateRangeCodec';
import { MapDetails } from '@models/MapFavorite/mapDetails.model';
import { MapFavoriteInfo } from '@models/MapFavorite/mapFavoriteInfo.model';
import { MapFavoriteSnapshot } from 'app/shared/components/map-favorite-bar/map-favorite-bar.component';
import { MapFavoriteComponent } from 'app/shared/components/map-favorite/map-favorite.component';
import { SensorService } from '@services/sensor.service';
import { SensorGroupService } from '@services/sensorgroup.service';
import { UserService } from '@services/user.service';
import { AppComponent } from 'app/app.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { SensorReadingsService } from '../sensor-readings/sensor-readings.service';
import moment from 'moment';
import { SensorWithGroupList } from '@models/Sensor/sensorWithGroupList.model';
import { select, sort } from 'd3';
import { ExportDataModalComponent } from '../sensors/export-data-modal/export-data-modal.component';
import { StatsReadingsModalComponent } from './stats-readings-modal/stats-readings-modal.component';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { GpsInfo } from '@models/Map/gpsInfo.model';
import { CommonShared } from 'app/shared/classes/common';
import { forkJoin, Subscription } from 'rxjs';
import { read } from 'fs';
import { ConditionFilter } from '@models/ReviewReadings/conditionFilter.model';
import { ChartFavoriteInfo } from '../../shared/models/ChartFavorite/chartfavoriteInfo.model';
import { ChartFavoriteRequest } from '../../shared/models/ChartFavorite/chartFavoriteRequest.model';
import { ChartDetails } from '../../shared/models/ChartFavorite/chartDetails.model';
import { ChartReadingsEchartModalComponent } from './chart-readings-echart-modal/chart-readings-echart-modal.component';
import { ChartTileComponent } from './chart-tile/chart-tile.component';
import { Authorization } from 'app/shared/classes/authorization';
import { AppMessageService } from '../../shared/services/message.service';
import { Message, MessageActionType, MessageType, AppComponentName } from '../../shared/models/Messages/message.model';
import { ChangeLogNotesParams, ChangeLogNotesComponent } from '../messages/changelog-notes/changelognotes.components';

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
};

// One chart on the Chart tab. Every tile charts the page's shared selection (sensors,
// groups, reading types, date range); a tile has its own favorite, name and chart options.
interface ChartTile {
  key: number;                          // identifies the tile (and its chart) while the page is open
  favorite: ChartFavoriteInfo | null;   // null until saved: an added chart or a duplicate
  name: string;
  savedChartDetails: ChartDetails;      // handed to the tile's chart, which applies it once
  darkBackground: boolean;
}

// What the "last used" snapshot keeps per open tile.
interface LastUsedTile {
  favoriteId?: number;
  name: string;
  chartDetails: string;         // the tile's chart options JSON (same shape favorites persist)
}

// Persisted "last used" page state so Review Readings can auto-restore on launch.
// (Timezone / original / calibrated already persist via their own shared localStorage
// keys and are re-applied by loadSavedSettings().)
interface ReviewReadingsLastUsed {
  // The first open tile's favorite and chart options. Kept so a snapshot written before
  // the Chart tab could show several charts still restores (as a single tile).
  favoriteId?: number;         // set when a saved favorite was the last selection
  tiles?: LastUsedTile[];      // every open tile, in order
  tilesClosedByUser?: boolean; // the user closed every tile, so none opens on its own
  sensorIds?: number[];
  sensorGroupIds?: number[];
  readingTypeIds?: number[];
  dateStart?: string;          // ISO
  dateEnd?: string;            // ISO
  dateRangeName?: string;
  filterReadingValuesText?: string;
  chartDetails?: string;       // eChart options JSON (same shape favorites persist)
  mapFavoriteId?: number;      // set when a saved map favorite was the last one shown on the GeoMap tab
  mapDetails?: MapDetails;     // the GeoMap tab's view and open popups
}

@Component({
  standalone: false,
  selector: 'app-review-readings',
  templateUrl: './review-readings.component.html',
  styleUrls: ['./review-readings.component.scss']
})

export class ReviewReadingsComponent implements OnInit, OnDestroy {

  @ViewChild(ExportDataModalComponent) exportDataModal: ExportDataModalComponent;
  @ViewChild(StatsReadingsModalComponent) statsReadingModal: StatsReadingsModalComponent;
  @ViewChildren(ChartTileComponent) tileRefs: QueryList<ChartTileComponent>;
  @ViewChild(MapFavoriteComponent) mapComponent: MapFavoriteComponent;

  // The GeoMap tab's position in the tabset (Stats=0, Chart=1, Table=2, GeoMap=3, Ask AI=4).
  private static readonly GEOMAP_TAB_INDEX = 3;

  common = new CommonShared();
  auth = new Authorization();

  sensorsAndGroupsDisplayed: SensorWithGroupList[] = [];
  readingTypesDisplayed: SensorReadingType[] = [];

  // "Sensors and Groups" panel resize: a user-set floor height (px, 0 = none)
  // that the drag handle adjusts; the effective cap is max(this, content height).
  @ViewChild('sensorsPanel') sensorsPanel: ElementRef;
  sensorsPanelManualHeight = 0;
  private sensorsResizing = false;
  private sensorsResizeStartY = 0;
  private sensorsResizeStartHeight = 0;

  allSensors: Sensor[] = [];
  allSensorGroups: SensorGroup[] = [];
  allAvailableReadingTypes: SensorReadingType[] = [];

  // All readings fetched so far, flattened from readingsCacheByTypeId. Derived -
  // rebuild it via rebuildAllAvailableReadings() rather than assigning directly.
  allAvailableReadings: SensorReadingInfo[] = [];

  // Readings already fetched this session, keyed by reading type ID. Readings are
  // loaded per reading type as the user checks them, and a type stays cached after
  // it is unchecked so re-checking it costs no API call. Cleared whenever the
  // sensors / date range / timezone change (see invalidateReadingsCache()).
  private readingsCacheByTypeId = new Map<number, SensorReadingInfo[]>();

  // Number of readings available per reading type for the selected sensors / date range / timezone,
  // shown beside each type in the Reading Types list before the user checks it. Filled by a background
  // request (see refreshReadingCounts); a type that has no entry yet shows a spinner while the request
  // is running. Types that were actually loaded are updated from the cache so the list matches the table.
  readingCountsByTypeId = new Map<number, number>();
  readingCountsLoading = false;
  private readingCountsSubscription: Subscription | null = null;

  // Reading type names that carry GPS coordinates. These are always fetched
  // alongside the user's selection so backfillMissingGpsLocations() can pair them
  // with the other readings (see GPS source #1 there).
  private static readonly LATITUDE_TYPES = ['latitude', 'lat'];
  private static readonly LONGITUDE_TYPES = ['longitude', 'long', 'lng'];

  selectedDateRange: DateRange = { startDate: moment().startOf('day'), endDate: moment().endOf('day') };
  selectedDateRangeName: string = 'Today';
  // Set when a preset range is clicked in the picker; consumed by the trailing
  // datesUpdated('') so it doesn't overwrite the preset label with 'Custom Range'.
  private rangeJustClicked = false;
  ranges = DateRanges;
  invalidDates: moment.Moment[] = [];
  selectedTimeZone: TimeZoneSelection = TimeZoneSelection.Local;
  selectedTimeZoneOffset: number = moment().utcOffset();

  searchSensorsText: string = '';
  searchReadingTypeText: string = '';
  username = 'unknown';

  selectedSensors: Sensor[] = [];
  selectedSensorGroups: SensorGroup[] = [];
  selectedReadingTypes: SensorReadingType[] = [];

  // Stable Date for the eChart [dateRangeStart] input. Cached so the template
  // does not create a new Date each change-detection cycle (which would loop the
  // child's ngOnChanges). Recomputed only when the date-range start changes.
  private _echartDateRangeStart: Date;
  private _echartDateRangeStartMs: number;
  get echartDateRangeStart(): Date {
    const start = this.selectedDateRange?.startDate;
    if (start == undefined) {
      return undefined;
    }
    const ms = start.valueOf();
    if (ms !== this._echartDateRangeStartMs) {
      this._echartDateRangeStartMs = ms;
      this._echartDateRangeStart = start.toDate();
    }
    return this._echartDateRangeStart;
  }
  selectedSensorReadings: SensorReadingInfo[] = [];
  filteredSensorReadings: SensorReadingInfo[] = [];

  // Per-sensor GPS-source counts (over the currently filtered readings) for the
  // GPS indicator icons beside the "Readings" label.
  //  reading  - reading's own gpsLocation (red)
  //  readings - derived from lat/long reading types (blue)
  //  sensor   - copied from the sensor's gpsLocation (green)
  //  none     - no GPS (gray)
  gpsCounts = { reading: 0, readings: 0, sensor: 0, none: 0 };

  showCalibratedReadings: boolean = true;
  showOriginalReadings: boolean = false;

  // Chart tab: the charts that are open, each in its own tile. They all chart the same
  // selection, so only favorites that share it can be opened together.
  tiles: ChartTile[] = [];
  private nextTileKey = 1;
  // Set when the user closes the last tile, so one is not opened again until they ask for one.
  private userClosedAllTiles = false;
  private static readonly NEW_CHART_NAME = 'New chart';

  // The dark-background choice a new tile starts with: the last one the user picked.
  private defaultDarkBackground = false;
  private static readonly ECHART_DARK_BACKGROUND_STORAGE_KEY = 'EchartDarkBackground';

  // Tiles per row: Small 3, Medium 2, Large 1 (remembered per browser).
  tileSize: 'Small' | 'Medium' | 'Large' = 'Medium';
  private static readonly TILE_SIZE_STORAGE_KEY = 'ReviewReadingsTileSize';
  // Chart heights follow the window's height: the full-page chart's own default height
  // (calc(92vh - 260px)) for Large, a share of it for the smaller sizes, each with a floor.
  private static readonly TILE_CHART_HEIGHTS = {
    Small: 'max(320px, calc((92vh - 260px) * 0.55))',
    Medium: 'max(360px, calc((92vh - 260px) * 0.75))',
    Large: 'max(420px, calc(92vh - 260px))'
  };

  // Chart Favorite (server-backed) state on the Chart tab. Mirrors the
  // sensor-readings ("Chart Group Readings") page so the same save/load/delete
  // UI can be reused here.
  public globalSwitch: boolean;
  public chartFavorites: ChartFavoriteInfo[] = [];
  public chartFavoritesNamesToDisplay: { id: number; name: string; userName: string; toGroupBy: string }[] = [];
  // The dropdown's choice. Choosing does not open it: Open, Duplicate and Delete act on it.
  public chartFavoriteChoice: any;
  // The tile whose Save / Export dialog is open.
  private savingTile: ChartTile | null = null;
  private exportingTile: ChartTile | null = null;
  public chartFavoritesLoaded = false;
  public userGroups: UserGroup[] = [];
  public associatedUserGroups: UserGroup[] = [];
  public isReadyAssociatedUserGroups = false;
  public selectedUserGroup: UserGroup;
  public favoriteName = '';
  public changeLogNotes = '';
  public preEditObjectAsJSONString = '';
  // After a create/update the favorites list reloads asynchronously; this holds
  // the just-saved favorite's name so it can be re-selected in the dropdown once
  // the reloaded list arrives (so the user still sees which favorite is loaded).
  private favoriteNameToSelectAfterLoad: string | undefined;

  // ---- Map favorites (GeoMap tab) ----
  // The selected map favorite, with its permissions. Mutually exclusive with the selected chart favorite.
  public selectedMapFavorite: MapFavoriteInfo | undefined;
  // The favorite remembered from the last visit, shown in the bar once its list has loaded.
  public mapFavoriteIdToShow: number | undefined;
  // The map view and open popups handed to the map (changed only when a favorite or remembered
  // state is applied, or when the tab is entered), and the map's own latest report.
  public mapSavedDetails: MapDetails | null = null;
  private latestMapDetails: MapDetails | null = null;
  // An applied favorite's view, waiting for its readings (see handPendingMapDetailsToMap).
  private pendingMapDetails: MapDetails | null = null;

  // Favorite save modal state.
  modalRef: BsModalRef;
  public isModalOpen = false;

  // Chart-data export state (Chart tab). The CSV is built from the chart's
  // visible series (including aggregates/sum) by the eChart component.
  exportChartModalRef: BsModalRef;
  exportChartFilename = 'chart-readings';
  config = {
    backdrop: false,
    ignoreBackdropClick: true
  };

  public userGroupconfig: any = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select User Group',
    noResultsFound: 'No User Groups found!',
    searchOnKey: 'name'
  };

  // Reusable payload buffer for create/update.
  public makefavoriteChartData: ChartFavoriteRequest = {
    id: undefined,
    name: undefined,
    isManualScaling: undefined,
    scaleYMin: undefined,
    scaleYMax: undefined,
    outlierYMin: undefined,
    outlierYMax: undefined,
    sensorGroupID: undefined,
    sensorGroupIDs: [],
    sensorIDs: [],
    sensorReadingTypeIDs: [],
    dateRange: undefined,
    chartDetails: undefined,
    userName: undefined,
    userGroupId: null
  };

  datesReady: boolean = false;
  sensorsReady: boolean = false;

  filterReadingValuesText: string = '';

  // The currently selected tab (Stats=0, Chart=1, Table=2, GeoMap=3, Ask AI=4).
  public activeTabIndex: number = 0;

  // Auto-restore of the last-used page state on launch.
  private static readonly LAST_USED_STORAGE_KEY = 'ReviewReadingsLastUsed';
  // The selected tab persists under its own key (independent of the selection
  // snapshot), so it survives a favorite-restore and is remembered even before
  // any sensors are selected.
  private static readonly ACTIVE_TAB_STORAGE_KEY = 'ReviewReadingsActiveTab';
  private sensorsLoaded = false;        // getAllSensors async arrived
  private groupsLoaded = false;         // getAllSensorGroups async arrived
  private lastUsedRestoreDone = false;  // restore runs at most once
  private restoreInProgress = false;    // suppress saveState() while rehydrating
  private pendingRestore: ReviewReadingsLastUsed | null = null;
  private pendingRestoreMissing: string[] = [];
  private pendingRestoreFavorite: ChartFavoriteInfo | null = null;   // favorite mid-apply
  private pendingRestoreReadingTypeIds: number[] | null = null;      // types to re-check after apply
  private pendingRestoreTiles: LastUsedTile[] = [];                    // saved tiles after the first, added once the first is restored
  
  public SensorsAndGroupsCols = [
    {field: 'checked', header: '', width: '10%'},
    {field: 'name', header: 'Sensors and Groups', width: '90%'},
  ];

  public SensorReadingTypesCols = [
    {field: 'checked', header: '', width: '10%'},
    {field: 'name', header: 'Available Sensor Reading Types', width: '66%'},
    {field: 'count', header: 'Readings', width: '24%'},
  ];


  constructor(public toastr: ToastrService, public modalService: BsModalService, public _sensorService: SensorService, public _sensorGroupService: SensorGroupService, public global: AppComponent, private confirmationService: ConfirmationService,
      public _userService: UserService, public _sensorReadingService: SensorReadingsService, private route: ActivatedRoute, public _appMessageService: AppMessageService) {
    this.globalSwitch = global.globalSwitch;
  }


  ngOnInit(): void {
  this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
    results => {
      this.username = results['value'];
      if (this.username != null && this.username != '') {
        this.startUp();
      }
    },
    err => {
      this.toastr.error('Error unable to read current user\'s info');
    });
  }


  ngOnDestroy(): void {
    this.readingCountsSubscription?.unsubscribe();
  }


  startUp() {
    this.loadSavedSettings();
    this.getAllSensors();
    this.getAllSensorGroups();
    this.getAssociatedUserGroups();
  }


  loadSavedSettings() {
    var showOriginalReadingsStr = localStorage.getItem('ShowOriginalReadings');
    var showCalibratedReadingsStr = localStorage.getItem('ShowCalibratedReadings');
    var timeZoneSelectedStr = localStorage.getItem('TimeZoneSelected');

    [this.selectedTimeZone, this.selectedTimeZoneOffset] = this.common.setTimeZoneSelection(timeZoneSelectedStr);
    this.showCalibratedReadings = (showCalibratedReadingsStr === 'true') ? true : false;
    this.showOriginalReadings = (showOriginalReadingsStr === '' || showOriginalReadingsStr == undefined || showOriginalReadingsStr === 'true') ? true : false;

    this.defaultDarkBackground = localStorage.getItem(ReviewReadingsComponent.ECHART_DARK_BACKGROUND_STORAGE_KEY) === 'true';

    const savedTileSize = localStorage.getItem(ReviewReadingsComponent.TILE_SIZE_STORAGE_KEY);
    if (savedTileSize === 'Small' || savedTileSize === 'Medium' || savedTileSize === 'Large') {
      this.tileSize = savedTileSize;
    }

    const savedTab = localStorage.getItem(ReviewReadingsComponent.ACTIVE_TAB_STORAGE_KEY);
    if (savedTab != undefined && savedTab !== '') {
      const tabIndex = Number(savedTab);
      if (!isNaN(tabIndex)) {
        this.activeTabIndex = tabIndex;
      }
    }
  }


  // Flip one tile between dark and light. The choice also becomes the background new tiles
  // start with, and is saved with the page state.
  onTileDarkToggle(tile: ChartTile) {
    tile.darkBackground = !tile.darkBackground;
    this.defaultDarkBackground = tile.darkBackground;
    localStorage.setItem(ReviewReadingsComponent.ECHART_DARK_BACKGROUND_STORAGE_KEY, tile.darkBackground ? 'true' : 'false');
    this.saveState();
  }


  // Re-persist the "last used" snapshot when the user changes a chart option in
  // the eChart component (chart type, aggregate toggles, colors, buckets, axes,
  // ignore-outlier bounds), so those choices survive a page reload.
  onEchartOptionsChanged() {
    this.saveState();
  }


  // ---- Chart tiles ---------------------------------------------------------

  tileChartHeight(): string {
    return ReviewReadingsComponent.TILE_CHART_HEIGHTS[this.tileSize];
  }


  onTileSizeChange(): void {
    localStorage.setItem(ReviewReadingsComponent.TILE_SIZE_STORAGE_KEY, this.tileSize);
  }


  private makeTile(favorite: ChartFavoriteInfo | null, name: string, details: ChartDetails): ChartTile {
    return {
      key: this.nextTileKey++,
      favorite,
      name,
      savedChartDetails: details,
      darkBackground: details.darkBackground ?? this.defaultDarkBackground
    };
  }


  // The chart that shows a tile, once Angular has created it.
  private chartOf(tile: ChartTile): ChartReadingsEchartModalComponent | undefined {
    return this.tileRefs?.find(t => t.tileKey === tile.key)?.chart;
  }


  trackTile(index: number, tile: ChartTile): number {
    return tile.key;
  }


  // A tile was dropped (dragged by its title bar): apply the new order and save it with the
  // page state, so it survives a reload.
  onTileDropped(drop: CdkDragDrop<ChartTile[]>): void {
    if (drop.previousIndex === drop.currentIndex) {
      return;
    }
    moveItemInArray(this.tiles, drop.previousIndex, drop.currentIndex);
    this.saveState();
  }


  // Whether the user may edit the saved favorite behind a tile (and so edit its labels).
  canEditTile(tile: ChartTile): boolean {
    return tile.favorite != undefined && tile.favorite.id > 0 && tile.favorite.userFeatureAccess?.['EditChart'] === true;
  }


  // The tile that already shows a saved favorite, if any.
  private tileOfFavorite(favoriteId: number): ChartTile | undefined {
    return this.tiles.find(t => t.favorite != undefined && t.favorite.id === favoriteId);
  }


  // Add a blank chart to the board.
  addNewTile(): void {
    this.userClosedAllTiles = false;
    this.tiles.push(this.makeTile(null, ReviewReadingsComponent.NEW_CHART_NAME, { echartType: 'line' }));
    this.afterTilesChanged();
  }


  closeTile(tile: ChartTile): void {
    this.tiles = this.tiles.filter(t => t !== tile);
    if (this.tiles.length === 0) {
      this.userClosedAllTiles = true;
    }
    this.afterTilesChanged();
  }


  // Open one chart when there is a usable selection and nothing is open, so the tab shows
  // a chart right away as it always has; not after the user closed the last one on purpose.
  private ensureDefaultTile(): void {
    if (this.tiles.length === 0 && !this.userClosedAllTiles && !this.restoreInProgress && this.isChartFavoriteReadyToSave()) {
      this.tiles.push(this.makeTile(null, ReviewReadingsComponent.NEW_CHART_NAME, { echartType: 'line' }));
      this.updateChartFavoritesList(this.globalSwitch);
    }
  }


  private afterTilesChanged(): void {
    this.updateChartFavoritesList(this.globalSwitch);
    this.saveState();
  }


  // ---- Chart Favorites (server-backed) ------------------------------------


  getAssociatedUserGroups(): void {
    this._userService.getAssociatedUserGroups(this.username).subscribe(
      results => {
        if (results != null) {
          this.isReadyAssociatedUserGroups = true;
          this.associatedUserGroups = results;
          this.getUserGroups();
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving user groups.');
      });
  }


  getUserGroups(): void {
    this._userService.getUserGroups().subscribe(
      results => {
        if (results != null) {
          this.userGroups = results;
          this.getChartFavorites();
        } else {
          this.toastr.warning('No user groups found');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving all user groups');
      });
  }


  getChartFavorites(): void {
    this._sensorReadingService.getChartFavorites().subscribe(
      results => {
        if (results != null) {
          this.chartFavorites = results;
          this.chartFavorites.forEach(c => {
            this.auth.mapUserAccessForChart(c, this.globalSwitch, this.username, this.userGroups);
          });
        } else {
          this.chartFavorites = [];
        }
        this.reselectSavedFavorite();
        this.refreshTileFavorites();
        this.updateChartFavoritesList(this.globalSwitch);
        this.chartFavoritesLoaded = true;
        this.tryRestoreLastUsed();
        return;
      },
      err => {
        this.reselectSavedFavorite();       // clears the pending selection (list didn't reload)
        this.chartFavoritesLoaded = true;   // don't block last-used restore on a favorites error
        this.tryRestoreLastUsed();
        this.toastr.error('Error occurred while retrieving chart favorites.');
      });
  }


  // After a create/update reloads the list, point the tile that was saved at its (new)
  // favorite, so its header shows the id and name and a further save updates it. Matched by
  // name (the create flow enforces name uniqueness). Also refreshes the pre-edit snapshot
  // so a subsequent update logs its changes correctly.
  private reselectSavedFavorite(): void {
    if (this.favoriteNameToSelectAfterLoad == undefined) {
      return;
    }

    const saved = this.chartFavorites.find(c => c.name === this.favoriteNameToSelectAfterLoad);
    if (saved != undefined && this.savingTile != undefined && this.tiles.includes(this.savingTile)) {
      this.savingTile.favorite = saved;
      this.savingTile.name = saved.name;
      this.preEditObjectAsJSONString = JSON.stringify(saved);
      this.saveState();
    }
    this.favoriteNameToSelectAfterLoad = undefined;
    this.savingTile = null;
  }


  // Keep each tile's favorite current (name, permissions) after the list reloads.
  private refreshTileFavorites(): void {
    this.tiles.forEach(tile => {
      if (tile.favorite != undefined) {
        tile.favorite = this.chartFavorites.find(f => f.id === tile.favorite.id) ?? tile.favorite;
      }
    });
  }


  // The full favorite behind the dropdown's choice (the dropdown only holds id and name).
  get choiceFavorite(): ChartFavoriteInfo | undefined {
    return (this.chartFavoriteChoice != undefined)
      ? this.chartFavorites.find(f => f.id === this.chartFavoriteChoice.id)
      : undefined;
  }


  // Whether a favorite charts the page's current selection: the same sensors, sensor groups,
  // reading types and date range. Only such favorites can be shown next to the open tiles,
  // because every tile charts the one selection in the left pane.
  private favoriteMatchesSelection(favorite: ChartFavoriteInfo): boolean {
    const ids = (items: { id: number }[]) => (items ?? []).map(x => x.id).sort((a, b) => a - b).join(',');
    const groups: SensorGroup[] = (favorite.sensorGroups != undefined && favorite.sensorGroups.length > 0)
      ? favorite.sensorGroups
      : (favorite.sensorGroup != undefined ? [favorite.sensorGroup] : []);

    return ids(favorite.sensors) === ids(this.selectedSensors)
      && ids(groups) === ids(this.selectedSensorGroups)
      && ids(favorite.sensorReadingTypes) === ids(this.selectedReadingTypes)
      && favorite.dateRange === this.encodeDateRange();
  }


  // Re-filter the dropdown when the selection moves under the open tiles.
  private refreshFavoritesFilter(): void {
    if (this.tiles.length > 0) {
      this.updateChartFavoritesList(this.globalSwitch);
    }
  }


  updateChartFavoritesList(showAll: boolean) {
    this.chartFavoritesNamesToDisplay = [];
    this.chartFavorites.forEach(chart => {
      // With a chart open, only favorites that share its selection can be opened alongside it.
      if (this.tiles.length > 0 && !this.favoriteMatchesSelection(chart)) {
        return;
      }
      if (chart.userFeatureAccess["ViewListing"] || showAll) {
        this.chartFavoritesNamesToDisplay.push({
          id: chart.id,
          name: chart.name,
          userName: chart.userName,
          toGroupBy: (chart.userGroupId == undefined || chart.userGroupId < 1) ? chart.userName : chart.userGroup
        });
      }
    });

    this.chartFavoritesNamesToDisplay.sort((a, b) => {
      if (b.toGroupBy > a.toGroupBy) { return -1; }
      if (b.toGroupBy < a.toGroupBy) { return 1; }
      if (b.name > a.name) { return -1; }
      if (b.name < a.name) { return 1; }
      return 0;
    });
  }


  // Open the favorite chosen in the dropdown as a tile. With no tile open it also sets the
  // left pane to the favorite's selection and loads its readings; with tiles open the
  // favorite must share their selection, so it just adds a tile.
  openChosenFavorite(): void {
    const favorite = this.choiceFavorite;
    if (favorite == undefined) {
      return;
    }

    if (this.tileOfFavorite(favorite.id) != undefined) {
      this.toastr.info('"' + favorite.name + '" is already open');
      return;
    }

    this.openFavoriteAsTile(favorite, false);
  }


  // Open an unsaved copy of the chosen favorite: same selection and chart options, no id,
  // saved as a new favorite when the user clicks Save.
  duplicateChosenFavorite(): void {
    const favorite = this.choiceFavorite;
    if (favorite == undefined) {
      return;
    }

    this.openFavoriteAsTile(favorite, true);
  }


  private openFavoriteAsTile(favorite: ChartFavoriteInfo, asCopy: boolean): void {
    this.userClosedAllTiles = false;

    if (this.tiles.length === 0) {
      // The left pane takes the favorite's selection; its readings load, and the first tile opens.
      this.applyFavorite(favorite, asCopy);
      return;
    }

    if (!this.favoriteMatchesSelection(favorite)) {
      this.toastr.info('"' + favorite.name + '" uses a different date range, sensors, sensor groups or reading types than the open charts');
      return;
    }

    // The readings are already loaded: just add the tile. Date range, sensors, reading types,
    // time zone and the original/calibrated choice belong to the whole page, so the favorite's
    // own values for them are not applied.
    this.tiles.push(this.tileFromFavorite(favorite, asCopy));
    this.afterTilesChanged();
  }


  private tileFromFavorite(favorite: ChartFavoriteInfo, asCopy: boolean): ChartTile {
    const details = this.chartOptionsOf(favorite.chartDetails,
      { isManual: favorite.isManualScaling, min: favorite.scaleYMin, max: favorite.scaleYMax });

    return asCopy
      ? this.makeTile(null, 'Copy of ' + favorite.name, { ...details })
      : this.makeTile(favorite, favorite.name, details);
  }


  // Shared favorite-apply used by both Open and last-used restore.
  // Seeds the sensor/group rows from the favorite's group ids + sensor ids,
  // applies the eChart options, sets the date range, then fetches reading types
  // (whose async success re-checks the saved types and loads readings).
  private applyFavorite(favorite: ChartFavoriteInfo, asCopy = false): void {
    this.restoreInProgress = true;
    // A chart favorite replaces a map favorite, including the one remembered from last time.
    this.selectedMapFavorite = undefined;
    this.mapFavoriteIdToShow = undefined;

    // Prefer the plural sensorGroups list; fall back to the single sensorGroup.
    const groups: SensorGroup[] = (favorite.sensorGroups != undefined && favorite.sensorGroups.length > 0)
      ? favorite.sensorGroups
      : (favorite.sensorGroup != undefined ? [favorite.sensorGroup] : []);

    this.seedSensorSelection(groups.map(g => g.id), (favorite.sensors ?? []).map(s => s.id));

    // The favorite's time zone and original/calibrated choice become the page's, and its
    // chart options go to a new tile (an unsaved copy when duplicating).
    this.applyHostOptions(this.parseChartDetails(favorite.chartDetails));
    this.tiles = [this.tileFromFavorite(favorite, asCopy)];
    this.updateChartFavoritesList(this.globalSwitch);

    // Date range (preset label or "start,end").
    this.applyDateRange(favorite.dateRange);
    this.datesReady = true;

    // A favorite defines a fresh sensors × date × timezone scope, so the readings
    // cached for the previous selection no longer apply — drop it so loadReadings()
    // fetches instead of rebuilding stale (wrong-sensor) rows from the cache.
    this.invalidateReadingsCache();

    // Fetch reading types, then re-check the favorite's saved types and load.
    this.pendingRestoreFavorite = favorite;
    this.pendingRestoreReadingTypeIds = (favorite.sensorReadingTypes ?? []).map(t => t.id);
    this.getReadingTypesForSensors(this.selectedSensors.map(x => x.id));
  }


  // Apply a map favorite from the GeoMap tab: the same selection a chart favorite restores
  // (sensors, groups, reading types, dates, time zone), plus the saved map view and popups,
  // which are handed to the map together with the readings they belong to
  // (see handPendingMapDetailsToMap).
  applyMapFavorite(favorite: MapFavoriteInfo): void {
    let details: MapDetails | null = null;
    try {
      details = (favorite.mapDetails != undefined && favorite.mapDetails !== '') ? JSON.parse(favorite.mapDetails) : null;
    } catch {
      details = null;
    }

    this.restoreInProgress = true;

    this.seedSensorSelection((favorite.sensorGroups ?? []).map(g => g.id), (favorite.sensors ?? []).map(s => s.id));

    this.applyDateRange(favorite.dateRange);
    this.datesReady = true;

    if (details?.timeZone != undefined) {
      [this.selectedTimeZone, this.selectedTimeZoneOffset] = this.common.setTimeZoneSelection(details.timeZone);
    }

    this.invalidateReadingsCache();

    // The favorite's view is cached right away (the map still shows the previous one until
    // it is handed over), and handed to the map once the readings have loaded.
    this.pendingMapDetails = details;
    this.latestMapDetails = details;

    // Same continuation as a chart favorite: re-check the saved reading types, then load.
    this.pendingRestoreFavorite = favorite as any;
    this.pendingRestoreReadingTypeIds = (favorite.sensorReadingTypes ?? []).map(t => t.id);
    this.getReadingTypesForSensors(this.selectedSensors.map(x => x.id));
  }


  // Seed the Sensors and Groups rows, checked, from a favorite's group ids and sensor ids
  // (shared by chart and map favorites).
  private seedSensorSelection(groupIds: number[], sensorIds: number[]): void {

    // Build the checked sensor/group rows (validating each still exists). Add the
    // groups first, then only add individual sensors that are NOT already covered
    // by one of those groups (so a group + its member sensors don't duplicate).
    // Group membership is resolved from allSensorGroups, which is fully hydrated
    // with sensors (the favorite's own group objects may not carry .sensors).
    const rows: SensorWithGroupList[] = [];
    const sensorIdsInSelectedGroups = new Set<number>();

    // Each favorite group becomes an expandable group row with only the
    // favorite's member sensors checked (a legacy favorite may include just a
    // subset of the group). A partial group is auto-expanded so the user sees
    // which members are selected.
    groupIds.forEach(id => {
      const group = this.allSensorGroups.find(x => x.id === id);
      if (group) {
        const groupSensorIds = (group.sensors ?? []).map(s => s.id);
        const checkedIds = groupSensorIds.filter(sid => sensorIds.includes(sid));
        const row = this.makeGroupRow(group, checkedIds);
        row.expanded = row.indeterminate === true;
        rows.push(row);
        groupSensorIds.forEach(sid => sensorIdsInSelectedGroups.add(sid));
      }
    });

    sensorIds.forEach(id => {
      const sensor = this.allSensors.find(x => x.id === id);
      // Skip sensors already represented as a member of one of the groups above.
      if (sensor && !sensorIdsInSelectedGroups.has(id)) {
        rows.push({ id: sensor.id, name: sensor.name, isSensor: true, isSensorGroup: false, checked: true, count: 1 });
      }
    });

    sort(rows, (a, b) => a.name.localeCompare(b.name));
    this.sensorsAndGroupsDisplayed = rows;
    this.updateSelectedSensors();
    this.sensorsReady = this.selectedSensors.length > 0;
  }


  // Continuation of applyFavorite(), invoked from getReadingTypesForSensors()'s
  // success once readingTypesDisplayed has been (re)populated: re-check the
  // favorite's saved reading types, then load readings.
  private finishFavoriteAfterReadingTypes(): void {
    const typeIds = this.pendingRestoreReadingTypeIds ?? [];
    this.pendingRestoreFavorite = null;
    this.pendingRestoreReadingTypeIds = null;

    this.readingTypesDisplayed.forEach(rt => {
      rt.checked = typeIds.includes(rt.id);
    });
    this.selectedReadingTypes = this.readingTypesDisplayed.filter(x => x.checked);

    // The first chart is in place; add the others that were open.
    this.restorePendingTiles();

    this.restoreInProgress = false;

    if (this.sensorsReady && this.datesReady) {
      this.loadReadings();
    }

    this.saveState();
  }


  // A manual sensor/reading-type edit means the selection is no longer a map favorite's, so
  // drop that association. The open charts stay open and chart the new selection.
  // Skipped while restoring/applying.
  private clearSelectedFavoriteOnManualChange(): void {
    if (this.restoreInProgress) {
      return;
    }
    this.selectedMapFavorite = undefined;
    this.mapFavoriteIdToShow = undefined;
  }


  // ---- Map favorites (the GeoMap tab) ----

  // The bar's dropdown reports the selected map favorite (picked, saved, deleted, cleared).
  onMapFavoriteSelected(favorite: MapFavoriteInfo | undefined): void {
    this.selectedMapFavorite = favorite;
    this.mapFavoriteIdToShow = favorite?.id;
    this.saveState();
  }


  // True when the selected map favorite exists and the user may edit it (labels).
  isMapFavoriteEditable(): boolean {
    return this.selectedMapFavorite != undefined
        && this.selectedMapFavorite.id > 0
        && this.selectedMapFavorite.userFeatureAccess?.['EditMapFavorite'] === true;
  }


  isMapFavoriteReadyToSave(): boolean {
    return this.selectedSensors.length > 0 && this.selectedReadingTypes.length > 0 && this.datesReady;
  }


  // The bar asks for this when the user clicks Create / Update: the page's selection plus the
  // live map (and the time zone, which decides which readings the favorite loads).
  readonly mapFavoriteSnapshot = (): MapFavoriteSnapshot | null => {
    return {
      sensorIDs: this.selectedSensors.map(s => s.id),
      sensorGroupIDs: this.selectedSensorGroups.map(g => g.id),
      sensorReadingTypeIDs: this.selectedReadingTypes.map(t => t.id),
      dateRange: this.encodeDateRange(),
      mapDetails: this.currentMapDetails()
    };
  };


  // The map reports every view / popup change; keep the latest so it can be restored when the
  // tab is entered again and saved with the page's state.
  onMapDetailsChanged(details: MapDetails): void {
    this.latestMapDetails = details;
    this.saveState();
  }


  private currentMapDetails(): MapDetails | undefined {
    const details = this.mapComponent?.getMapDetails() ?? this.latestMapDetails ?? undefined;
    return details != undefined ? { ...details, timeZone: this.selectedTimeZone } : undefined;
  }


  // Once the readings for an applied map favorite are in, give the map the favorite's view and
  // popups. They go in the same change as the readings, so the map can find its pins and
  // reopen the popups; handing them over earlier would apply them to the previous pins.
  private handPendingMapDetailsToMap(): void {
    if (this.pendingMapDetails == null) {
      return;
    }
    this.mapSavedDetails = this.pendingMapDetails;
    this.pendingMapDetails = null;
  }


  // Open the save dialog for one tile's chart: an update when the tile shows a favorite the
  // user may edit, otherwise a new favorite.
  saveChartFavorite(tile: ChartTile, template: TemplateRef<any>): void {
    this.savingTile = tile;
    const favorite = tile.favorite;

    // What a later update logs its changes against: the favorite as it was when the dialog opened.
    this.preEditObjectAsJSONString = (favorite != undefined) ? JSON.stringify(favorite) : '';

    if (favorite == undefined || favorite.userGroupId == undefined || favorite.userGroupId < 1) {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id == -1);
    } else {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id == favorite.userGroupId);
    }

    if (this.isUpdate()) {
      this.favoriteName = favorite.name;
    } else if (favorite == undefined && tile.name !== ReviewReadingsComponent.NEW_CHART_NAME) {
      this.favoriteName = tile.name;      // a duplicate starts as "Copy of ..."
    }
    if (!this.modalRef || !this.isModalOpen) {
      this.isModalOpen = true;
      this.modalRef = this.modalService.show(template, this.config);
    }
  }


  // Open the (filename-only) dialog for exporting one tile's chart data series.
  openChartExport(tile: ChartTile, template: TemplateRef<any>): void {
    this.exportingTile = tile;
    this.exportChartFilename = this.safeFileName(tile.name) || 'chart-readings';
    this.exportChartModalRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true });
  }


  private safeFileName(name: string): string {
    return (name ?? '').replace(/[\\/:*?"<>|]/g, '').trim();
  }


  // Build the CSV from the chart's currently-visible series (raw + aggregates +
  // sum) and download it.
  exportChartDataWithOptions(): void {
    const filename = (this.exportChartFilename ?? '').trim();
    if (filename === '') {
      this.toastr.error('Please enter a valid filename');
      return;
    }
    const chart = (this.exportingTile != undefined) ? this.chartOf(this.exportingTile) : undefined;
    if (chart == undefined) {
      this.toastr.warning('No chart to export');
      return;
    }

    const csv = chart.getChartExportCsv();
    if (csv === '') {
      this.toastr.warning('No chart data to export');
      return;
    }

    this.exportChartModalRef.hide();
    this.saveData(csv, filename + '.csv');
  }


  // Download a string as a file (UTF-8 BOM so Excel reads it correctly).
  private saveData(dataString: string, fileName: string): void {
    const blob = new Blob(['﻿', dataString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);
  }


  addOrUpdateChartFavorite(): void {
    if (this.isUpdate()) {
      this.setChartFavoriteRequest();
      this.updateChartFavorite();
    } else {
      if (this.favoriteName.trim()) {
        if (!this.chartFavorites.some(x => x.name === this.favoriteName.trim())) {
          this.setChartFavoriteRequest();
          this.addFavorite();
        } else {
          this.toastr.error('Chart favorite name already exists. Please select a new one');
        }
      } else {
        this.toastr.error('Please enter a valid name');
      }
    }

    this.favoriteName = undefined;
  }


  // Whether the open save dialog updates a favorite (rather than creating one): its tile shows
  // a saved favorite the user may edit.
  isUpdate(): boolean {
    return this.savingTile != undefined && this.canEditTile(this.savingTile);
  }


  updateChartFavorite(): void {
    this._sensorReadingService.updateChartFavorite(this.makefavoriteChartData, this.username).subscribe(
      result => {
        if (result === 'success') {
          this.createMessageData(MessageActionType.Edited, this.makefavoriteChartData, this.getEditChanges(this.makefavoriteChartData));
          this.modalRef.hide();
          this.isModalOpen = false;
          this.toastr.success('Chart favorite updated successfully.');
          // Keep the updated favorite selected in the dropdown after the reload.
          this.favoriteNameToSelectAfterLoad = this.makefavoriteChartData.name;
          this.getChartFavorites();
        } else {
          this.toastr.warning('No Chart favorites found to update.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while updating chart favorite.');
      });
  }


  addFavorite(): void {
    this._sensorReadingService.createChartFavorite(this.makefavoriteChartData).subscribe(
      result => {
        if (result === 'success') {
          this.createMessageData(MessageActionType.New, this.makefavoriteChartData, '');
          this.modalRef.hide();
          this.isModalOpen = false;
          this.toastr.success('Chart favorite created successfully.');
          // Keep the newly created favorite selected in the dropdown after the reload.
          this.favoriteNameToSelectAfterLoad = this.makefavoriteChartData.name;
          this.getChartFavorites();
        } else {
          this.toastr.warning('No Chart favorites found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while saving chart favorite');
      });
  }


  // Build the create/update payload from the current selection. review-readings
  // supports a free mix of loose sensors and/or multiple groups, so all selected
  // group ids go into sensorGroupIDs[] (with the legacy scalar sensorGroupID = the
  // first selected group, or null when only individual sensors are selected),
  // and all selected sensor ids into sensorIDs[]. Manual-scaling / outlier
  // columns stay at their defaults here — those live in ChartDetails for this
  // page (the eChart owns them).
  setChartFavoriteRequest(): void {
    const groupIds = this.selectedSensorGroups.map(g => g.id);

    this.makefavoriteChartData.name = this.favoriteName;
    this.makefavoriteChartData.id = (this.savingTile?.favorite != undefined) ? this.savingTile.favorite.id : -1;
    this.makefavoriteChartData.userName = this.username;
    this.makefavoriteChartData.userGroupId = (this.selectedUserGroup == undefined || this.selectedUserGroup.id < 0) ? null : this.selectedUserGroup.id;

    this.makefavoriteChartData.sensorGroupID = (groupIds.length > 0) ? groupIds[0] : null;
    this.makefavoriteChartData.sensorGroupIDs = groupIds;
    this.makefavoriteChartData.sensorIDs = this.selectedSensors.map(x => x.id);
    this.makefavoriteChartData.sensorReadingTypeIDs = this.selectedReadingTypes.map(x => x.id);

    this.makefavoriteChartData.isManualScaling = false;
    this.makefavoriteChartData.scaleYMin = undefined;
    this.makefavoriteChartData.scaleYMax = undefined;
    this.makefavoriteChartData.outlierYMin = undefined;
    this.makefavoriteChartData.outlierYMax = undefined;

    this.makefavoriteChartData.dateRange = this.encodeDateRange();
    this.makefavoriteChartData.chartDetails = this.buildChartDetails(this.savingTile);
  }


  onDeleteChartFavoriteConfirm() {
    this.confirmationService.confirm({
      message: 'Please confirm that you want to delete the chart favorite "' + (this.choiceFavorite?.name ?? '') + '"',
      key: 'cd1',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.removeChartFavorite();
      },
      reject: () => {
      }
    });
  }


  // Delete the favorite chosen in the dropdown. A tile that shows it stays open, as an
  // unsaved chart.
  removeChartFavorite(): void {
    const favorite = this.choiceFavorite;
    if (favorite != undefined && favorite.name != null && favorite.name.length > 0) {
      this._sensorReadingService.deleteChartFavorite(favorite.id, this.username).subscribe(
        result => {
          if (result === 'success') {
            this.createMessageData(MessageActionType.Deleted, favorite as any, '');
            this.toastr.success('Chart favorite deleted');
            this.tiles.filter(t => t.favorite?.id === favorite.id).forEach(t => t.favorite = null);
            this.chartFavoriteChoice = undefined;
            this.getChartFavorites();
            this.saveState();
          } else {
            this.toastr.warning('No Chart favorites found.');
          }
          return;
        },
        err => {
        });
    }
  }


  onFavoriteCancelButton(): void {
    this.modalRef.hide();
    this.isModalOpen = false;
    this.savingTile = null;
  }


  isChartFavoriteReadyToSave(): boolean {
    return this.selectedSensors != undefined && this.selectedSensors.length > 0
      && this.selectedReadingTypes != undefined && this.selectedReadingTypes.length > 0
      && this.datesReady;
  }


  addNotes2ChangeLog() {
    this.changeLogNotes = '';

    const params: ChangeLogNotesParams = {
      changeLogNotes: this.changeLogNotes,
      userGroupId: 0,
      category: ''
    };

    var modalRef = this.modalService.show(ChangeLogNotesComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, class: 'modal-above-confirmdialog', initialState: params });
    modalRef.content.onChangeLogNotes.subscribe((value) => {
      this.changeLogNotes = value;
    });
  }


  createMessageData(msgActionType: MessageActionType, chart: ChartFavoriteRequest, msgNote: string) {
    const message: Message = {
      id: 0,
      messageType: MessageType.Audit,
      appComponentName: AppComponentName.Charts,
      actionType: msgActionType,
      appComponentItem: chart.name,
      details: msgNote,
      userNotes: this.changeLogNotes,
      count: 1,
      acknowledged: false,
      userName: this.username,
      userGroupId: chart.userGroupId
    };

    this._appMessageService.createAppMessage(message).subscribe(
      results => { return; },
      err => { });
  }


  getEditChanges(chart: ChartFavoriteRequest): string {
    // The pre-edit snapshot is only captured when a favorite is picked from the
    // dropdown; on a last-used restore it can be empty. Guard the parse so an
    // empty/unparseable snapshot yields "no detected changes" rather than
    // throwing (which would abort the update callback before the modal closes).
    if (this.preEditObjectAsJSONString == undefined || this.preEditObjectAsJSONString === '') {
      return '';
    }

    let original: ChartFavoriteRequest;
    try {
      original = JSON.parse(this.preEditObjectAsJSONString);
    } catch {
      return '';
    }

    let results = '';
    results += this.common.compareProperty('Name', original.name, chart.name);
    results += this.common.compareProperty('DateRange', original.dateRange, chart.dateRange);

    return results;
  }


  // Build the ChartDetails JSON string persisted on a favorite / localStorage for one tile:
  // its chart's current options merged with the host-level display options (timezone,
  // readings source, dark mode). A tile whose chart has not rendered yet keeps the options
  // it was opened with, so saving early does not blank them.
  private buildChartDetails(tile: ChartTile): string {
    const chart = this.chartOf(tile);
    const details: ChartDetails = (chart != undefined) ? chart.getChartDetails() : { ...(tile.savedChartDetails ?? {}) };

    details.timeZone = this.selectedTimeZone;
    details.timeZoneOffset = this.selectedTimeZoneOffset;
    details.showOriginalReadings = this.showOriginalReadings;
    details.showCalibratedReadings = this.showCalibratedReadings;
    details.darkBackground = tile.darkBackground;

    return JSON.stringify(details);
  }


  // Apply the host-level options of a favorite's ChartDetails (timezone, reading source) to
  // the page. These belong to the whole page, so they are applied when the board is first
  // set from a favorite, not when another favorite is added to it.
  private applyHostOptions(details: ChartDetails | undefined): void {
    if (details == undefined) {
      return;
    }

    if (details.timeZone != undefined) {
      [this.selectedTimeZone, this.selectedTimeZoneOffset] = this.common.setTimeZoneSelection(details.timeZone);
    }
    if (details.showOriginalReadings != undefined) {
      this.showOriginalReadings = details.showOriginalReadings;
    }
    if (details.showCalibratedReadings != undefined) {
      this.showCalibratedReadings = details.showCalibratedReadings;
    }
  }


  // Parse a favorite's ChartDetails JSON into the options handed to its tile's chart, which
  // applies them once the chart (re)builds. A null/empty/unparseable value (legacy favorite)
  // falls back to a line chart. A favorite's legacy single-axis manual scaling
  // (isManualScaling/scaleYMin/scaleYMax) is migrated onto the primary 'Value'
  // axis of the new per-axis scaling for backwards compatibility.
  private chartOptionsOf(chartDetails: string, legacyScaling?: { isManual: boolean; min: number; max: number }): ChartDetails {
    const details = this.parseChartDetails(chartDetails);
    if (details == undefined) {
      const saved: ChartDetails = { echartType: 'line' };
      this.migrateLegacyManualScaling(saved, legacyScaling);
      return saved;
    }

    this.migrateLegacyManualScaling(details, legacyScaling);
    return details;
  }


  // Backwards compatibility: when a favorite carries the old single-axis manual
  // scaling but no new per-axis map, migrate its bounds onto the primary 'Value'
  // axis so the eChart still honors them.
  private migrateLegacyManualScaling(details: ChartDetails, legacy?: { isManual: boolean; min: number; max: number }): void {
    if (legacy == undefined || legacy.isManual !== true) {
      return;
    }
    if (details.manualScaleByAxis != undefined && Object.keys(details.manualScaleByAxis).length > 0) {
      return; // new per-axis scaling already present; prefer it
    }
    const min = this.legacyScaleToNumber(legacy.min);
    const max = this.legacyScaleToNumber(legacy.max);
    if (min == null && max == null) {
      return;
    }
    details.manualScaleByAxis = { Value: { min, max } };
  }


  // Coerce a legacy scale bound (possibly string/null/blank) to a finite number
  // or null.
  private legacyScaleToNumber(value: any): number | null {
    if (value == undefined || value.toString().trim() === '') {
      return null;
    }
    const num = Number(value);
    return isNaN(num) ? null : num;
  }


  private parseChartDetails(chartDetails: string): ChartDetails | undefined {
    if (chartDetails == undefined || chartDetails === '') {
      return undefined;
    }
    try {
      return JSON.parse(chartDetails) ?? undefined;
    } catch {
      return undefined;
    }
  }


  // Encode the current date range the way favorites store it: a named preset
  // label when one is selected, otherwise a "start,end" ISO string.
  private encodeDateRange(): string {
    return encodeDateRangeValue(this.selectedDateRangeName, this.selectedDateRange);
  }


  // Apply an encoded date range (preset label or "start,end") — inverse of
  // encodeDateRange(). Shared by favorite and last-used restore.
  private applyDateRange(dateRange: string): void {
    // The shared codec also knows the "Last N hrs" quick picks and always builds fresh
    // moments (the DateRanges constant is evaluated once at load and goes stale).
    const decoded = decodeDateRange(dateRange);
    if (decoded == null) {
      return;
    }
    this.selectedDateRange = decoded.range;
    this.selectedDateRangeName = decoded.name;
  }


  // Track and persist the selected tab (under its own key, independent of the
  // selection snapshot, so it is always remembered).
  onTabSelect(index: number) {
    this.activeTabIndex = index;
    localStorage.setItem(ReviewReadingsComponent.ACTIVE_TAB_STORAGE_KEY, index.toString());

    // The map only exists while its tab is active (a map created in a hidden tab has no
    // size), so it is created again on every visit: give it the view it was left with.
    if (index === ReviewReadingsComponent.GEOMAP_TAB_INDEX && this.pendingMapDetails == null && this.latestMapDetails != null) {
      this.mapSavedDetails = this.latestMapDetails;
    }
  }


  getAllSensors(): void {
    this.allSensors = [];
    this._sensorService.getAllSensorsWithoutTypes().subscribe(
      results => {
        if (results != null) {
          this.allSensors = results;
          this.allSensors.forEach(x => {
            x.lastReading = { sensorId: x.id, isLoaded: false, date: null, displayColor: "black", waitingForLoad: false };
            if (x.name == null || x.name == '') {
              x.name = x.uuid;
            }
          });
          this.sensorsLoaded = true;
          this.tryRestoreLastUsed();
        } else {
          this.toastr.warning('No sensors found on server');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving sensor list');
      });
  }


  getAllSensorGroups(): void {
    this._sensorGroupService.getSensorGroupsWithSensors().subscribe(
      results => {
        if (results != null) {
          this.allSensorGroups = results;
          this.groupsLoaded = true;
          this.tryRestoreLastUsed();
        } else {
          this.toastr.warning('No Sensor Groups found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving current display.');
      });
  }


  // Restore the last-used state once sensors, sensor groups, AND chart favorites
  // have all loaded (favorites are needed so a last-used favorite can restore).
  private tryRestoreLastUsed(): void {
    if (this.lastUsedRestoreDone || !this.sensorsLoaded || !this.groupsLoaded || !this.chartFavoritesLoaded) {
      return;
    }
    this.lastUsedRestoreDone = true;

    if (!this.applyLinkedSelection()) {
      this.restoreState();
    }
  }


  // A link (e.g. from the home page search) can open this page on one chart favorite
  // (?chartFavoriteId=) or one sensor (?sensorId=) instead of the last-used state. Returns true when it
  // handled the page, so the last-used restore is skipped.
  private applyLinkedSelection(): boolean {
    const params = this.route.snapshot.queryParamMap;
    const favoriteId = Number(params.get('chartFavoriteId'));
    const sensorId = Number(params.get('sensorId'));

    if (favoriteId > 0) {
      const favorite = this.chartFavorites.find(f => f.id === favoriteId);
      if (favorite != undefined) {
        this.applyFavorite(favorite);
        return true;
      }
      this.toastr.warning('That chart favorite is no longer available.');
      return false;
    }

    if (sensorId > 0) {
      if (this.allSensors.some(s => s.id === sensorId)) {
        // Selects the sensor as if it had been ticked; the reading types are then the user's to pick.
        this.seedSensorSelection([], [sensorId]);
        this.getReadingTypesForSensors(this.selectedSensors.map(x => x.id));
        return true;
      }
      this.toastr.warning('That sensor is no longer available.');
      return false;
    }

    return false;
  }


  // Persist the current page state to localStorage. Only persists a usable
  // selection, and never while a restore is mid-flight.
  saveState(): void {
    if (this.restoreInProgress || this.selectedSensors.length === 0) {
      return;
    }

    const tileStates: LastUsedTile[] = this.tiles.map(tile => ({
      favoriteId: tile.favorite?.id,
      name: tile.name,
      chartDetails: this.buildChartDetails(tile)
    }));

    const state: ReviewReadingsLastUsed = {
      // The first tile is also kept in the original fields, so this snapshot still restores in
      // the shape older ones used.
      favoriteId: tileStates[0]?.favoriteId,
      tiles: tileStates,
      tilesClosedByUser: this.userClosedAllTiles,
      // Persist checked loose sensors plus the checked member sensors of any
      // group (so a group's selected subset round-trips on restore), and the ids
      // of groups that have at least one checked member.
      sensorIds: [
        ...this.sensorsAndGroupsDisplayed.filter(x => x.isSensor && x.checked).map(x => x.id),
        ...this.sensorsAndGroupsDisplayed
            .filter(x => x.isSensorGroup)
            .flatMap(g => (g.children ?? []).filter(c => c.checked).map(c => c.id))
      ],
      sensorGroupIds: this.sensorsAndGroupsDisplayed
          .filter(x => x.isSensorGroup && (x.children ?? []).some(c => c.checked))
          .map(x => x.id),
      readingTypeIds: this.selectedReadingTypes.map(x => x.id),
      dateStart: this.selectedDateRange.startDate.toISOString(),
      dateEnd: this.selectedDateRange.endDate.toISOString(),
      dateRangeName: this.selectedDateRangeName,
      filterReadingValuesText: this.filterReadingValuesText,
      chartDetails: tileStates[0]?.chartDetails,
      // Until the favorites list has loaded (right after a reload) the remembered favorite is
      // still waiting to be shown; keep it rather than dropping it.
      mapFavoriteId: this.selectedMapFavorite?.id ?? this.mapFavoriteIdToShow,
      mapDetails: this.pendingMapDetails ?? this.currentMapDetails()
    };

    localStorage.setItem(ReviewReadingsComponent.LAST_USED_STORAGE_KEY, JSON.stringify(state));
  }


  // Restore the last-used state: seed checked sensor/group rows, then fetch
  // reading types; the rest continues in finishRestoreAfterReadingTypes() once
  // the (async) reading types arrive.
  private restoreState(): void {
    const raw = localStorage.getItem(ReviewReadingsComponent.LAST_USED_STORAGE_KEY);
    if (raw == undefined || raw === '') {
      return;
    }

    let s: ReviewReadingsLastUsed;
    try {
      s = JSON.parse(raw);
    } catch {
      return;
    }
    if (s == undefined) {
      return;
    }

    // The GeoMap tab's remembered view and favorite. Read before the chart-favorite early
    // return below, so they survive that path too. The map is only created when its tab is
    // opened, so it picks the view up from mapSavedDetails then.
    this.mapFavoriteIdToShow = s.mapFavoriteId;
    this.latestMapDetails = s.mapDetails ?? null;
    this.mapSavedDetails = s.mapDetails ?? null;

    this.userClosedAllTiles = s.tilesClosedByUser === true;

    // The charts that were open. A snapshot written before the Chart tab could show several
    // has none listed: its favorite and chart options make the one tile.
    const savedTiles: LastUsedTile[] = (s.tiles != undefined)
      ? s.tiles
      : (s.chartDetails != undefined || s.favoriteId != undefined)
        ? [{ favoriteId: s.favoriteId, name: ReviewReadingsComponent.NEW_CHART_NAME, chartDetails: s.chartDetails }]
        : [];
    this.pendingRestoreTiles = savedTiles.slice(1);

    // Last used was a saved favorite that still exists → reuse the favorite path.
    if (s.favoriteId != undefined && s.favoriteId > 0) {
      const favorite = this.chartFavorites.find(f => f.id === s.favoriteId);
      if (favorite != undefined) {
        this.applyFavorite(favorite);
        return;
      }
    }

    this.restoreInProgress = true;
    this.pendingRestoreMissing = [];

    // Seed the sensor/group rows directly (checked), validating existence.
    // Group rows are rebuilt as expandable rows with only the saved member
    // sensors checked (the saved sensorIds carry the group's checked subset).
    const rows: SensorWithGroupList[] = [];
    const savedSensorIds = s.sensorIds ?? [];
    const sensorIdsInGroups = new Set<number>();

    (s.sensorGroupIds ?? []).forEach(id => {
      const group = this.allSensorGroups.find(x => x.id === id);
      if (group) {
        const groupSensorIds = (group.sensors ?? []).map(x => x.id);
        const checkedIds = groupSensorIds.filter(sid => savedSensorIds.includes(sid));
        const row = this.makeGroupRow(group, checkedIds);
        row.expanded = row.indeterminate === true;
        rows.push(row);
        groupSensorIds.forEach(sid => sensorIdsInGroups.add(sid));
      } else {
        this.pendingRestoreMissing.push('group #' + id);
      }
    });

    savedSensorIds.forEach(id => {
      if (sensorIdsInGroups.has(id)) {
        return; // already represented as a member of a restored group
      }
      const sensor = this.allSensors.find(x => x.id === id);
      if (sensor) {
        rows.push({ id: sensor.id, name: sensor.name, isSensor: true, isSensorGroup: false, checked: true, count: 1 });
      } else {
        this.pendingRestoreMissing.push('sensor #' + id);
      }
    });

    if (rows.length === 0) {
      this.restoreInProgress = false;   // nothing restorable
      return;
    }

    sort(rows, (a, b) => a.name.localeCompare(b.name));
    this.sensorsAndGroupsDisplayed = rows;

    // Rebuild selectedSensors from the checked rows, then fetch reading types.
    this.updateSelectedSensors();
    this.sensorsReady = this.selectedSensors.length > 0;

    this.pendingRestore = s;
    this.getReadingTypesForSensors(this.selectedSensors.map(x => x.id));
  }


  // Add the saved tiles that follow the first one: a saved favorite that still exists shows
  // its own saved options; one that was deleted since, or a chart that was never saved, comes
  // back as an unsaved chart with the options the snapshot kept.
  private restorePendingTiles(): void {
    const saved = this.pendingRestoreTiles;
    this.pendingRestoreTiles = [];

    saved.forEach(item => {
      const favorite = (item.favoriteId != undefined && item.favoriteId > 0)
        ? this.chartFavorites.find(f => f.id === item.favoriteId)
        : undefined;

      if (favorite != undefined) {
        if (this.tileOfFavorite(favorite.id) == undefined) {
          this.tiles.push(this.tileFromFavorite(favorite, false));
        }
        return;
      }

      const details = this.parseChartDetails(item.chartDetails) ?? { echartType: 'line' };
      this.tiles.push(this.makeTile(null, item.name || ReviewReadingsComponent.NEW_CHART_NAME, details));
    });

    this.updateChartFavoritesList(this.globalSwitch);
  }


  // Continuation of restoreState(), invoked from getReadingTypesForSensors()'s
  // success once readingTypesDisplayed has been (re)populated.
  private finishRestoreAfterReadingTypes(): void {
    const s = this.pendingRestore;
    this.pendingRestore = null;
    if (s == undefined) {
      this.restoreInProgress = false;
      return;
    }

    // Re-check the saved reading types on the freshly-loaded rows.
    const typeIds = s.readingTypeIds ?? [];
    const present = new Set<number>();
    this.readingTypesDisplayed.forEach(rt => {
      if (typeIds.includes(rt.id)) {
        rt.checked = true;
        present.add(rt.id);
      }
    });
    typeIds.filter(id => !present.has(id)).forEach(id => this.pendingRestoreMissing.push('reading type #' + id));
    this.selectedReadingTypes = this.readingTypesDisplayed.filter(x => x.checked);

    // Restore the date range (rehydrate moments from ISO).
    if (s.dateStart != undefined && s.dateEnd != undefined) {
      this.selectedDateRange = { startDate: moment(s.dateStart), endDate: moment(s.dateEnd) };
      this.selectedDateRangeName = s.dateRangeName ?? 'Custom Range';
    }
    this.datesReady = true;

    // Synchronous UI preferences.
    this.filterReadingValuesText = s.filterReadingValuesText ?? '';

    // Restore the first chart: its saved options (chart type, aggregates, colors, etc.), with
    // the page's timezone / reading source from the same payload. A saved favorite that was
    // deleted since comes back as an unsaved chart. With no chart saved, one opens on its own
    // (unless the user had closed them all).
    const firstSaved = (s.tiles != undefined) ? s.tiles[0] : undefined;
    const firstDetails = this.parseChartDetails(firstSaved?.chartDetails ?? s.chartDetails);
    this.applyHostOptions(firstDetails);
    this.tiles = [];
    if (firstSaved != undefined || s.chartDetails != undefined) {
      this.tiles.push(this.makeTile(null, firstSaved?.name ?? ReviewReadingsComponent.NEW_CHART_NAME, firstDetails ?? { echartType: 'line' }));
    }
    this.restorePendingTiles();

    if (this.pendingRestoreMissing.length > 0) {
      this.toastr.info('Some saved selections are no longer available and were skipped: ' + this.pendingRestoreMissing.join(', '));
    }

    this.restoreInProgress = false;

    // Auto-load readings (its success runs readingTypeSelectionChanged(), which
    // recomputes selectedSensorReadings and applies the restored filter text).
    if (this.sensorsReady && this.datesReady) {
      this.loadReadings();
    }
  }


  getReadingTypesForSensors(sensorIds: number[]): void {
    this._sensorService.getReadingTypesForSensors(sensorIds, '').subscribe(
      results => {
        if (results != null) {
          this.allAvailableReadingTypes = results;
          this.common.sortArrayByTypeParm(this.allAvailableReadingTypes);
          this.readingTypesDisplayed = this.allAvailableReadingTypes;

          // Continue a favorite-apply once reading types are available.
          if (this.pendingRestoreFavorite) {
            this.finishFavoriteAfterReadingTypes();
          }
          // Continue a last-used restore-in-progress once reading types are available.
          else if (this.pendingRestore) {
            this.finishRestoreAfterReadingTypes();
          }

          // After the continuations above, so a favorite / restored state has set its date range first.
          this.refreshReadingCounts();
        } else {
          this.readingCountsLoading = false;
          this.toastr.warning('No reading types found for given sensors found');
        }
        return;
      },
      err => {
        this.readingCountsLoading = false;
        this.toastr.error('Error occurred while retrieving sensor data types');
      });
  }


  sensorSearchTextChanged() {
    // Keep rows that have a selection: checked sensors/groups, and groups with a
    // checked member (a partial group's own checkbox is unchecked/indeterminate).
    const isSelected = (row: SensorWithGroupList) =>
      row.checked || (row.isSensorGroup && (row.children ?? []).some(c => c.checked));
    const kept = this.sensorsAndGroupsDisplayed.filter(isSelected);
    const keptSensorIds = new Set(kept.filter(r => r.isSensor).map(r => r.id));
    const keptGroupIds = new Set(kept.filter(r => r.isSensorGroup).map(r => r.id));

    this.searchSensorsText = this.searchSensorsText.trim().toLowerCase();

    var sensors = this.allSensors.filter(x => x.name.toLowerCase().includes(this.searchSensorsText) || x.uuid.toLowerCase().includes(this.searchSensorsText));
    sensors = sensors.slice(0, 500).filter(x => !keptSensorIds.has(x.id));

    var sensorGroups = this.allSensorGroups.filter(x => x.name.toLowerCase().includes(this.searchSensorsText));
    sensorGroups = sensorGroups.slice(0, 150).filter(x => !keptGroupIds.has(x.id));

    this.sensorsAndGroupsDisplayed = kept.concat(sensors.map(sensor => ({
      id: sensor.id,
      name: sensor.name,
      isSensor: true,
      isSensorGroup: false,
      checked: false,
      count: 1
    })));

    this.sensorsAndGroupsDisplayed = this.sensorsAndGroupsDisplayed.concat(sensorGroups.map(group => this.makeGroupRow(group, [])));

    sort(this.sensorsAndGroupsDisplayed, (a, b) => a.name.localeCompare(b.name));
  }


  readingTypeSearchTextChanged() {
    this.searchReadingTypeText = this.searchReadingTypeText.trim().toLowerCase();
    this.readingTypesDisplayed = this.allAvailableReadingTypes.filter(x => x.type.toLowerCase().includes(this.searchReadingTypeText));
  }


  isInvalidDate = (m: moment.Moment) => this.invalidDates.some(d => d.isSame(m, 'day'));

  datesUpdated(quickPick: string) {
    if (quickPick === 'Last 4 hrs') {
      this.selectedDateRange = { startDate: moment().add(-4 , 'hours'), endDate: moment() };
    }
    else if (quickPick === 'Last 8 hrs') {
      this.selectedDateRange = { startDate: moment().add(-8 , 'hours'), endDate: moment() };
    }
    else if (quickPick === 'Last 12 hrs') {
      this.selectedDateRange = { startDate: moment().add(-12 , 'hours'), endDate: moment() };
    }
    else if (quickPick === 'Last 24 hrs') {
      this.selectedDateRange = { startDate: moment().add(-24 , 'hours'), endDate: moment() };
    }
    
    if (quickPick !== '') {
      this.selectedDateRangeName = quickPick;
    } else if (this.rangeJustClicked) {
      // Trailing event right after a preset was clicked in the picker: keep the
      // label set by setRangeSelected() so the favorite saves the rolling preset
      // (e.g. "Last 7 Days") instead of a fixed start,end range.
      this.rangeJustClicked = false;
    } else {
      this.selectedDateRangeName = 'Custom Range';
    }

    this.datesReady = true;

    // The cached readings were fetched for the previous date range.
    this.invalidateReadingsCache();
    this.refreshReadingCounts();

    if (this.sensorsReady && this.datesReady) {
      this.loadReadings();
    }
    this.saveState();
  }


  setRangeSelected(selectedRange: any) {
    this.selectedDateRangeName = selectedRange.label;
    // Survive the trailing datesUpdated('') so it keeps this preset label.
    this.rangeJustClicked = true;

    // rangeClicked emits { label, dates: [startMoment, endMoment] } -- map it into the
    // { startDate, endDate } shape the rest of the component (and saveState) expects.
    // Assigning the raw event left startDate/endDate undefined and crashed saveState().
    if (selectedRange.dates != undefined && selectedRange.dates.length === 2) {
      this.selectedDateRange = { startDate: selectedRange.dates[0], endDate: selectedRange.dates[1] };
    }

    // The date range moved, so the cached readings no longer cover it. The picker
    // also raises (datesUpdated), which is what actually reloads.
    this.invalidateReadingsCache();

    this.saveState();
  }


  sensorSelectionChanged() {
    this.clearSelectedFavoriteOnManualChange();
    this.updateSelectedSensors();

    // The cached readings belong to the previous sensor selection.
    this.invalidateReadingsCache();

    // The counts too. The new ones are requested once the reading types for these sensors arrive; until
    // then the list shows spinners rather than the previous sensors' numbers.
    this.resetReadingCounts(this.selectedSensors.length > 0);

    this.getReadingTypesForSensors(this.selectedSensors.map(x => x.id));

    if (this.selectedSensors.length > 0) {
      this.sensorsReady = true;
    }

    // No load here: readings are fetched per reading type, and the reading types for
    // these sensors are still on their way back from getReadingTypesForSensors().
    // Checking a reading type (or a favorite / last-used restore) drives the load.
    this.recomputeSelectedReadings();
    this.saveState();
  }


  // User-driven reading-type change (checkbox / select-all). Drops any loaded
  // favorite association, then loads whatever the new selection needs.
  readingTypeSelectionChanged() {
    this.clearSelectedFavoriteOnManualChange();
    this.recomputeSelectedReadings();

    // Fetches only the newly-checked types; a no-op when they are already cached.
    if (this.sensorsReady && this.datesReady) {
      this.loadReadings();
    }
  }


  // Recompute the selected/filtered readings from the current selection. Does NOT
  // clear the selected favorite, so it is safe to call from internal flows (e.g.
  // after readings load) without wiping the favorite dropdown.
  private recomputeSelectedReadings() {
    this.selectedReadingTypes = this.readingTypesDisplayed.filter(x => x.checked);

    // A usable selection with nothing open shows a chart; open tiles limit which favorites
    // the dropdown offers.
    this.ensureDefaultTile();
    this.refreshFavoritesFilter();

    this.selectedSensorReadings = this.allAvailableReadings.filter(reading =>
      this.selectedReadingTypes.some(type => type.id === reading.typeID) &&
      this.selectedSensors.some(sensor => sensor.id === reading.sensorID)
    );

    this.executeFilterReadingValues();
    this.saveState();
  }


  selectedTimeZoneChanged() {
    switch (this.selectedTimeZone) {
      case TimeZoneSelection.Local:
        localStorage.setItem('TimeZoneSelected', 'Local');
        this.selectedTimeZoneOffset = moment().utcOffset();
        break;
    
    case TimeZoneSelection.UTC:
      localStorage.setItem('TimeZoneSelected', 'UTC');
        this.selectedTimeZoneOffset = 0;
        break;

    case TimeZoneSelection.Source:
      localStorage.setItem('TimeZoneSelected', 'Source');
        this.selectedTimeZoneOffset = 0;
        break;

      default:
        this.selectedTimeZoneOffset = 0;
        break;
    }

    // Timezone is a server-side query parameter (it shifts and re-windows the
    // timestamps), so the cached readings have to be refetched.
    this.invalidateReadingsCache();
    this.refreshReadingCounts();

    if (this.sensorsReady && this.datesReady) {
      this.loadReadings();
    }
    this.saveState();
  }


  selectedReadingSourceChanged() {
    localStorage.setItem('ShowOriginalReadings', this.showOriginalReadings.toString());
    localStorage.setItem('ShowCalibratedReadings', this.showCalibratedReadings.toString());

    // Conservative: original/calibrated only picks which value the tabs render, so
    // the cached readings would still be valid -- but this preserves the existing
    // refetch behavior rather than changing it as a side effect.
    this.invalidateReadingsCache();

    this.loadReadings();
    this.saveState();
  }


  updateSelectedSensors() {
    this.selectedSensors = [];
    this.selectedSensorGroups = [];

    // Add a sensor (by id) to selectedSensors, deduped: a sensor may be selected
    // both individually and via a group, so only add it once (otherwise the
    // chart/stats/table double it).
    const addSensor = (id: number) => {
      const sensor = this.allSensors.find(s => s.id === id);
      if (sensor && !this.selectedSensors.some(s => s.id === sensor.id)) {
        sensor.displayedSensorName = this.common.getSensorDisplayName(sensor);
        this.selectedSensors.push(sensor);
      }
    };

    this.sensorsAndGroupsDisplayed.forEach(row => {
      if (row.isSensor && row.checked) {
        addSensor(row.id);
      }

      if (row.isSensorGroup) {
        // A group contributes its individually-checked member sensors. A group
        // with any checked member is still a "selected group" so per-group
        // aggregate lines keep working (they partition selectedSensors by group).
        const checkedChildren = (row.children ?? []).filter(c => c.checked);
        if (checkedChildren.length > 0) {
          const group = this.allSensorGroups.find(g => g.id === row.id);
          if (group) {
            this.selectedSensorGroups.push(group);
          }
          checkedChildren.forEach(c => addSensor(c.id));
        }
      }
    });
  }


  // Build a "Sensors and Groups" row for a sensor group, with a child row per
  // member sensor. A child is checked when its id is in checkedSensorIds (pass
  // all member ids for a whole-group selection, a subset for a partial favorite,
  // or [] for an unchecked search result). The group's own checked/indeterminate
  // state is derived from its children.
  private makeGroupRow(group: SensorGroup, checkedSensorIds: number[]): SensorWithGroupList {
    const children: SensorWithGroupList[] = (group.sensors ?? []).map(s => ({
      id: s.id,
      name: s.name,
      isSensor: true,
      isSensorGroup: false,
      checked: checkedSensorIds.includes(s.id),
      count: 1
    }));
    const checkedCount = children.filter(c => c.checked).length;

    return {
      id: group.id,
      name: group.name,
      isSensor: false,
      isSensorGroup: true,
      count: group.sensors != undefined ? group.sensors.length : 0,
      children,
      expanded: false,
      checked: children.length > 0 && checkedCount === children.length,
      indeterminate: checkedCount > 0 && checkedCount < children.length
    };
  }


  // A group checkbox was toggled: cascade to all member sensors and clear the
  // partial (indeterminate) state.
  onGroupChecked(group: SensorWithGroupList): void {
    (group.children ?? []).forEach(c => c.checked = group.checked);
    group.indeterminate = false;
    this.sensorSelectionChanged();
  }


  // A group's member sensor checkbox was toggled: recompute the group's
  // checked/indeterminate state from its children.
  onGroupChildChecked(group: SensorWithGroupList): void {
    const children = group.children ?? [];
    const checkedCount = children.filter(c => c.checked).length;
    group.checked = children.length > 0 && checkedCount === children.length;
    group.indeterminate = checkedCount > 0 && checkedCount < children.length;
    this.sensorSelectionChanged();
  }


  // Expand / collapse a group row to show/hide its member sensors.
  toggleGroupExpanded(group: SensorWithGroupList): void {
    group.expanded = !group.expanded;
  }


  // Count of a group row's currently-checked member sensors (for the "(x/y)"
  // label in the picker).
  groupCheckedCount(group: SensorWithGroupList): number {
    return (group.children ?? []).filter(c => c.checked).length;
  }


  // Total rows visible in the "Sensors and Groups" list, counting the member
  // rows of any expanded group so the panel can grow to show them.
  get visibleSensorRowCount(): number {
    let count = this.sensorsAndGroupsDisplayed.length;
    this.sensorsAndGroupsDisplayed.forEach(row => {
      if (row.isSensorGroup && row.expanded) {
        count += (row.children ?? []).length;
      }
    });
    return count;
  }


  // Effective max-height cap for the "Sensors and Groups" panel: the larger of
  // the content-driven height (grows when a group is expanded) and the user's
  // dragged height.
  get sensorsPanelMaxHeightPx(): number {
    const contentHeight = this.visibleSensorRowCount * 43 + 130;
    return Math.max(contentHeight, this.sensorsPanelManualHeight);
  }


  // Drag-to-resize the "Sensors and Groups" panel: start from its current
  // rendered height, then track the pointer.
  startSensorsResize(event: MouseEvent): void {
    this.sensorsResizing = true;
    this.sensorsResizeStartY = event.clientY;
    this.sensorsResizeStartHeight = (this.sensorsPanel != undefined)
      ? this.sensorsPanel.nativeElement.clientHeight
      : this.sensorsPanelMaxHeightPx;
    event.preventDefault();
  }


  @HostListener('document:mousemove', ['$event'])
  onSensorsResizeMove(event: MouseEvent): void {
    if (!this.sensorsResizing) {
      return;
    }
    const delta = event.clientY - this.sensorsResizeStartY;
    this.sensorsPanelManualHeight = Math.max(160, this.sensorsResizeStartHeight + delta);
  }


  @HostListener('document:mouseup')
  onSensorsResizeEnd(): void {
    this.sensorsResizing = false;
  }


  // loadReadings() {
  //   var forkJoinServices: any[] = [];
  //   this.allAvailableReadings = [];

  //   this.selectedSensors.forEach(sensor => {
  //     const sensorReadingCriteria: SensorReadingCriteria = {
  //       sensorId: sensor.id,
  //       startDate: this.selectedDateRange.startDate.format('YYYY-MM-DD HH:mm'), 
  //       endDate: this.selectedDateRange.endDate.format('YYYY-MM-DD HH:mm'), 
  //       timeZoneDisplay: this.selectedTimeZone,
  //       timeZoneOffset: this.selectedTimeZoneOffset
  //     };

  //     forkJoinServices.push( this._sensorService.getSensorReadingsForSensor(sensorReadingCriteria) );
  //   });

  //   forkJoin(forkJoinServices).subscribe(
  //     results => {
  //       results.forEach(readingsSet => {
  //         var sensorReadings: SensorReadingInfo[] = readingsSet;
  //         sensorReadings = sensorReadings.filter(s => s.value != 'nan' && s.value != 'NaN' && s.origValue != 'nan' && s.origValue != 'NaN');
  //         sensorReadings.forEach((reading, index) => {
  //           reading.createdDate = new Date(reading.createdDate);
  //           reading.sensorName = this.common.getSensorDisplayName(this.selectedSensors.find(s => s.id === reading.sensorID));
  //           reading.value = Number(reading.value).toPrecision(9);
  //           reading.origValue = Number(reading.origValue).toPrecision(9);
  //           reading.id = index + (sensorReadings.length * results.indexOf(readingsSet));
  //         });

  //         this.allAvailableReadings = this.allAvailableReadings.concat(sensorReadings);
  //         this.readingTypeSelectionChanged();  // filters sensor readings based on selected types
  //       });

  //       this.allAvailableReadings.sort((a, b) => {
  //         if (b.createdDate > a.createdDate) {
  //           return 1;
  //         }
  //         if (b.createdDate < a.createdDate) {
  //           return -1;
  //         }
  //       });

  //       this.allAvailableReadings = [...this.allAvailableReadings]; // Trigger change detection
  //     });

  //   if (this.allAvailableReadings.length > 0) {
  //     this.toastr.success(this.allAvailableReadings.length + ' sensor readings loaded successfully');
  //   }
  // }


  // Drop every cached reading. Call whenever something that scopes the fetched
  // data changes (sensors, date range, timezone), because the cache is only valid
  // for one (sensors x dates x timezone) combination.
  private invalidateReadingsCache(): void {
    this.readingsCacheByTypeId.clear();
    this.allAvailableReadings = [];
  }


  // Discard the cached readings and refetch the current selection. Readings are
  // live data, so a long session would otherwise keep showing the snapshot taken
  // when each reading type was first checked.
  refreshReadings(): void {
    this.invalidateReadingsCache();
    this.refreshReadingCounts();
    this.loadReadings();
  }


  // Cancel any count request in flight and forget the counts. `pending` keeps the spinners showing
  // (used when a new request is about to follow).
  private resetReadingCounts(pending: boolean = false): void {
    this.readingCountsSubscription?.unsubscribe();
    this.readingCountsSubscription = null;
    this.readingCountsByTypeId.clear();
    this.readingCountsLoading = pending;
  }


  // Ask the API, in the background, how many readings each reading type has for the selected sensors,
  // date range and timezone, so the Reading Types list can show it before a type is checked. A newer
  // request cancels an older one. Failures are quiet: the counts are a convenience, so the list simply
  // shows no numbers.
  private refreshReadingCounts(): void {
    this.resetReadingCounts();

    const range = this.selectedDateRange;
    if (this.selectedSensors.length === 0 || (this.allAvailableReadingTypes ?? []).length === 0
        || range == undefined || !moment.isMoment(range.startDate) || !moment.isMoment(range.endDate)) {
      return;
    }

    const criteria: SensorReadingCriteria2 = {
      sensorIds: this.selectedSensors.map(sensor => sensor.id),
      startDate: range.startDate.format('YYYY-MM-DD HH:mm'),
      endDate: range.endDate.format('YYYY-MM-DD HH:mm'),
      timeZoneDisplay: this.selectedTimeZone,
      timeZoneOffset: this.selectedTimeZoneOffset
    };

    this.readingCountsLoading = true;

    this.readingCountsSubscription = this._sensorService.getReadingCountsForSensors(criteria).subscribe(
      results => {
        this.readingCountsLoading = false;

        // null: the endpoint answered 404 (e.g. an older API). Show nothing rather than a wrong "(0)".
        if (results == null) {
          return;
        }

        // Types whose readings have already been loaded keep the exact count taken from the cache.
        (results as { id: number, count: number }[]).forEach(r => {
          if (!this.readingCountsByTypeId.has(r.id)) {
            this.readingCountsByTypeId.set(r.id, r.count);
          }
        });

        // The API only returns types that have readings in range; the rest have none.
        this.allAvailableReadingTypes.forEach(rt => {
          if (!this.readingCountsByTypeId.has(rt.id)) {
            this.readingCountsByTypeId.set(rt.id, 0);
          }
        });
      },
      err => {
        this.readingCountsLoading = false;
      });
  }


  // The count to show beside a reading type, or undefined while it is not known yet.
  getReadingCount(readingTypeId: number): number | undefined {
    return this.readingCountsByTypeId.get(readingTypeId);
  }


  // Flatten the per-type cache into the single array the rest of the page reads.
  // Sorted newest-first, and re-indexed afterwards because `id` is the p-table row
  // key and the dedupe key in executeFilterReadingValues() -- it has to be unique
  // across the merged set, not just within one reading type's batch.
  private rebuildAllAvailableReadings(): void {
    // Append one element at a time rather than merged.push(...readings): spreading a
    // large array into a call passes each element as a function argument, and a single
    // reading type can hold 100K+ points (many sensors x a wide date range), which
    // overflows the argument/stack limit -> "Maximum call stack size exceeded".
    const merged: SensorReadingInfo[] = [];
    this.readingsCacheByTypeId.forEach(readings => {
      for (const reading of readings) {
        merged.push(reading);
      }
    });

    merged.sort((a, b) => {
      if (b.createdDate > a.createdDate) {
        return 1;
      }
      if (b.createdDate < a.createdDate) {
        return -1;
      }
      return 0;
    });

    merged.forEach((reading, index) => reading.id = index);

    this.allAvailableReadings = merged;
  }


  // The reading types that need to be loaded for the current selection: the user's
  // checked types plus any latitude/longitude types the selected sensors expose, so
  // GPS pairing in backfillMissingGpsLocations() keeps working even when the user
  // has not checked lat/long themselves.
  private getReadingTypesToLoad(): SensorReadingType[] {
    const needed = [ ...this.selectedReadingTypes ];

    (this.allAvailableReadingTypes ?? []).forEach(rt => {
      const type = (rt.type != undefined) ? rt.type.toLowerCase() : '';
      const isGpsType = ReviewReadingsComponent.LATITUDE_TYPES.includes(type)
                     || ReviewReadingsComponent.LONGITUDE_TYPES.includes(type);
      if (isGpsType && !needed.some(x => x.id === rt.id)) {
        needed.push(rt);
      }
    });

    return needed;
  }


  // Load readings for the currently-selected reading types that are not already
  // cached, in a single batched request. When everything needed is cached (e.g. the
  // user re-checked a type they had unchecked) this does no API call at all and just
  // recomputes from the cache.
  loadReadings() {
    // Nothing to show without both a sensor and a reading type. The reading types are
    // checked second, so this is the normal state right after a sensor selection --
    // and it also keeps the lat/long companion types from being fetched on their own.
    if (this.selectedSensors.length === 0 || this.selectedReadingTypes.length === 0) {
      return;
    }

    const missingTypes = this.getReadingTypesToLoad().filter(rt => !this.readingsCacheByTypeId.has(rt.id));

    if (missingTypes.length === 0) {
      // Everything is cached -- just re-derive from it.
      this.rebuildAllAvailableReadings();
      this.backfillMissingGpsLocations(this.allAvailableReadings);
      this.recomputeSelectedReadings();
      this.handPendingMapDetailsToMap();
      return;
    }

    var selectedSensorIds = this.selectedSensors.map(sensor => sensor.id);

    const sensorReadingCriteria: SensorReadingCriteria2 = {
      sensorIds: selectedSensorIds,
      startDate: this.selectedDateRange.startDate.format('YYYY-MM-DD HH:mm'),
      endDate: this.selectedDateRange.endDate.format('YYYY-MM-DD HH:mm'),
      timeZoneDisplay: this.selectedTimeZone,
      timeZoneOffset: this.selectedTimeZoneOffset,
      // Only the types not yet loaded -- the API returns all types when this is empty.
      sensorReadingTypes: missingTypes
    };

    // Create a map with sensor IDs as keys and the corresponding sensor
    this._sensorService.getSensorReadingsForSensor(sensorReadingCriteria).subscribe(
      results => {
        // Build a lookup map for sensor names (O(1) per reading instead of O(m))
        const sensorNameMap = new Map<number, string>();
        this.selectedSensors.forEach(s => sensorNameMap.set(s.id, this.common.getSensorDisplayName(s)));

        const loadedReadings: SensorReadingInfo[] = results.filter(s => s.value != 'nan' && s.value != 'NaN' && s.origValue != 'nan' && s.origValue != 'NaN');
        loadedReadings.forEach(reading => {
          reading.createdDate = new Date(reading.createdDate);
          reading.sensorName = sensorNameMap.get(reading.sensorID) || '';
          reading.value = Number(reading.value).toPrecision(9);
          reading.origValue = Number(reading.origValue).toPrecision(9);
        });

        // Seed an entry for every requested type first, so a type with no readings
        // in this date range is still treated as loaded and is not re-requested
        // every time the user clicks it.
        missingTypes.forEach(rt => this.readingsCacheByTypeId.set(rt.id, []));
        loadedReadings.forEach(reading => {
          const cached = this.readingsCacheByTypeId.get(reading.typeID);
          if (cached != undefined) {
            cached.push(reading);
          }
          else {
            this.readingsCacheByTypeId.set(reading.typeID, [reading]);
          }
        });

        // The types just loaded now have an exact count (what the table will show), so the Reading Types
        // list uses it in place of the background estimate.
        missingTypes.forEach(rt => this.readingCountsByTypeId.set(rt.id, this.readingsCacheByTypeId.get(rt.id)?.length ?? 0));

        this.rebuildAllAvailableReadings();

        // Over the merged set, not just this batch: a latitude/longitude reading may
        // have arrived in an earlier batch than the reading it annotates.
        this.backfillMissingGpsLocations(this.allAvailableReadings);

        // Internal recompute (does NOT clear a loaded favorite selection).
        this.recomputeSelectedReadings();  // filters sensor readings based on selected types

        // A map favorite's view and popups go to the map together with its readings.
        this.handPendingMapDetailsToMap();

        if (loadedReadings.length > 0) {
          this.toastr.success(loadedReadings.length + ' sensor readings loaded successfully');
        }
      },
      err => {
        // Nothing was cached for these types, so the next selection change retries them.
        this.toastr.error('Error occurred while retrieving sensor readings. Error: ' + err.message);
      });
  }


  clearFilterReadingText() {
    this.filterReadingValuesText = '';
    this.executeFilterReadingValues();
  }


  executeFilterReadingValues() {
    if (this.filterReadingValuesText.trim() === '') {
      this.filteredSensorReadings = this.selectedSensorReadings;
      this.updateGpsCounts();
      this.saveState();
      return;
    }

    // e.g. "temp_f > 20 and temp_f < 40, humidity < 50 or humidity > 80, ph >= 6.5"
    const subSearchText = this.filterReadingValuesText.split(/[,;]+/);

    this.filteredSensorReadings = [];

    subSearchText.forEach(condition => {
      var parsedConditions = this.parseConditionSearch(condition);
      var filteredReadings = this.filterSensorReadingsUsingConditionFilter(parsedConditions);
      this.filteredSensorReadings = this.filteredSensorReadings.concat(filteredReadings);
    });

    const uniqueObjectsById = Object.values(this.filteredSensorReadings.reduce((accumulator, currentItem) => {
      if (accumulator.findIndex(x => x.id == currentItem.id) < 0) {
        accumulator.push(currentItem);
      }
      return accumulator;
    }, []));

    this.filteredSensorReadings = uniqueObjectsById;
    this.updateGpsCounts();
    this.saveState();
  }


  // True when a reading has usable (non-0/0) GPS coordinates.
  private readingHasGps(r: SensorReadingInfo): boolean {
    return r.gpsLocation != undefined
      && (Math.abs(r.gpsLocation.gpsLat) > 0 || Math.abs(r.gpsLocation.gpsLong) > 0);
  }


  // Resolve each reading's GPS by priority and tag it with gpsSource:
  //   1. latitude/longitude reading types (same sensor + timestamp) -> 'readings'
  //   2. the reading's own gpsLocation           -> 'reading'
  //   3. the owning sensor's gpsLocation          -> 'sensor'
  //   4. none                                     -> 'none'
  // Coordinates from sources 1 and 3 are backfilled onto the reading so the map
  // and chart can plot them. Multi-sensor: lat/long pair within a sensor only.
  backfillMissingGpsLocations(readings: SensorReadingInfo[]) {
    if (readings == undefined || readings.length === 0) {
      return;
    }

    const latTypes = ReviewReadingsComponent.LATITUDE_TYPES;
    const longTypes = ReviewReadingsComponent.LONGITUDE_TYPES;

    // Index lat/long values by "<uuid>|<timestampMs>" so they only pair within
    // the same sensor.
    const latByKey = new Map<string, number>();
    const longByKey = new Map<string, number>();

    readings.forEach(r => {
      const type = (r.type != undefined) ? r.type.toLowerCase() : '';
      const value = Number(r.value);
      if (isNaN(value)) {
        return;
      }
      const key = r.uuid + '|' + new Date(r.createdDate).getTime();
      if (latTypes.includes(type)) {
        latByKey.set(key, value);
      } else if (longTypes.includes(type)) {
        longByKey.set(key, value);
      }
    });

    // Sensor gpsLocation by uuid, for source #3.
    const sensorGpsByUuid = new Map<string, GpsInfo>();
    (this.selectedSensors ?? []).forEach(s => {
      if (s.gpsLocation != undefined
          && (Math.abs(s.gpsLocation.gpsLat) > 0 || Math.abs(s.gpsLocation.gpsLong) > 0)) {
        sensorGpsByUuid.set(s.uuid, s.gpsLocation);
      }
    });

    readings.forEach(r => {
      // Remember the GPS the reading itself carries before steps 1 and 3 overwrite gpsLocation.
      // First pass only: gpsSource is undefined until a reading has been backfilled once.
      if (r.gpsSource === undefined && this.readingHasGps(r)) {
        r.ownGpsLocation = r.gpsLocation;
      }

      // 1. latitude/longitude reading types
      const key = r.uuid + '|' + new Date(r.createdDate).getTime();
      const lat = latByKey.get(key);
      const long = longByKey.get(key);
      if (lat != undefined || long != undefined) {
        r.gpsLocation = { id: 0, gpsLat: lat ?? 0, gpsLong: long ?? 0, gpsAlt: 0, locationDescription: '' };
        r.gpsSource = 'readings';
        return;
      }

      // 2. the reading's own gpsLocation
      if (this.readingHasGps(r)) {
        r.gpsSource = 'reading';
        return;
      }

      // 3. the owning sensor's gpsLocation
      const sensorGps = sensorGpsByUuid.get(r.uuid);
      if (sensorGps != undefined) {
        r.gpsLocation = { ...sensorGps };
        r.gpsSource = 'sensor';
        return;
      }

      // 4. none
      r.gpsSource = 'none';
    });
  }


  // Count, over the currently filtered readings, how many sensors fall in each
  // GPS-source state. Per sensor, the highest-priority source seen wins
  // (readings > reading > sensor > none).
  updateGpsCounts() {
    const priority = { readings: 3, reading: 2, sensor: 1, none: 0 };
    const bySensor = new Map<string, 'reading' | 'readings' | 'sensor' | 'none'>();

    (this.filteredSensorReadings ?? []).forEach(r => {
      const state = r.gpsSource ?? 'none';
      const current = bySensor.get(r.uuid);
      if (current == undefined || priority[state] > priority[current]) {
        bySensor.set(r.uuid, state);
      }
    });

    const counts = { reading: 0, readings: 0, sensor: 0, none: 0 };
    bySensor.forEach(state => counts[state]++);
    this.gpsCounts = counts;
  }


  parseConditionSearch(searchText: string): ConditionFilter[] {
    var conditions: ConditionFilter[] = [];

    searchText = searchText.trim().toLowerCase();
    if (searchText === '') {
      return conditions;
    }

    while (searchText.length > 0) {
      var stopPosition = 0;

      for (let i = 0; i < searchText.length; i++) {
        if (searchText.substring(i, i + 4) === ' and') {
          stopPosition = i+4;
        }

        if (searchText.substring(i, i + 3) === ' or') {
          stopPosition = i+3;
        }

        if (i === searchText.length - 1) {
          stopPosition = searchText.length;
        }

        if (stopPosition > 0) {
          var conditionText = searchText.substring(0, stopPosition).trim();
          searchText = searchText.substring(stopPosition + 1).trim();
          var conditionParts = conditionText.split(' ');

          var condition: ConditionFilter = {
            type: (conditionParts.length > 0) ? conditionParts[0] : '',
            operator: (conditionParts.length > 1) ? conditionParts[1] : '',
            value: (conditionParts.length > 2) ? conditionParts[2] : '',
            logical: (conditionParts.length > 3) ? conditionParts[3] : ''
          };

          conditions.push(condition);
          break;
        }
      }
    }

    return conditions;
  }


  filterSensorReadingsUsingConditionFilter(parsedConditions: ConditionFilter[]): SensorReadingInfo[] {
    var filteredReadings: SensorReadingInfo[] = [];

    if (parsedConditions.length === 0) {
      filteredReadings = this.selectedSensorReadings;
      return filteredReadings;
    }

    parsedConditions.forEach((condition, index) => {
      var readings: SensorReadingInfo[] = [];

      var processAsORCondition = (index == 0 || parsedConditions[index-1].logical === 'or') ? true : false;

      switch (condition.operator) {
        case '=':
        case '==':
          
          readings = (processAsORCondition)
                      ? this.selectedSensorReadings.filter(reading => reading.type.toLowerCase() === condition.type && Number(reading.value) === Number(condition.value))
                      : filteredReadings.filter(reading => reading.type.toLowerCase() === condition.type && Number(reading.value) === Number(condition.value));
          break;

        case '!=':
        case '!==':
        case '<>':
          readings = (processAsORCondition)
                      ? this.selectedSensorReadings.filter(reading => reading.type.toLowerCase() === condition.type && Number(reading.value) != Number(condition.value))
                      : filteredReadings.filter(reading => reading.type.toLowerCase() === condition.type && Number(reading.value) != Number(condition.value));
          break;

        case '>':
          readings = (processAsORCondition)
                      ? this.selectedSensorReadings.filter(reading => reading.type.toLowerCase() === condition.type && Number(reading.value) > Number(condition.value))
                      : filteredReadings.filter(reading => reading.type.toLowerCase() === condition.type && Number(reading.value) > Number(condition.value));
          break;

        case '<':
          readings = (processAsORCondition)
                      ? this.selectedSensorReadings.filter(reading => reading.type.toLowerCase() === condition.type && Number(reading.value) < Number(condition.value))
                      : filteredReadings.filter(reading => reading.type.toLowerCase() === condition.type && Number(reading.value) < Number(condition.value));
          break;

        case '>=':
          readings = (processAsORCondition)
                      ? this.selectedSensorReadings.filter(reading => reading.type.toLowerCase() === condition.type && Number(reading.value) >= Number(condition.value))
                      : filteredReadings.filter(reading => reading.type.toLowerCase() === condition.type && Number(reading.value) >= Number(condition.value));
          break;

        case '<=':
          readings = (processAsORCondition)
                      ? this.selectedSensorReadings.filter(reading => reading.type.toLowerCase() === condition.type && Number(reading.value) <= Number(condition.value))
                      : filteredReadings.filter(reading => reading.type.toLowerCase() === condition.type && Number(reading.value) <= Number(condition.value));
          break;
      }

      if (processAsORCondition) {
        filteredReadings = filteredReadings.concat(readings);
      }
      else {
        filteredReadings = [...readings];
      }

    });

    return filteredReadings;
  }

  
  toggleCheckedSensors() {
    var newState = !this.sensorsAndGroupsDisplayed[0].checked;
    this.sensorsAndGroupsDisplayed.forEach(row => {
      row.checked = newState;
      if (row.isSensorGroup) {
        row.indeterminate = false;
        (row.children ?? []).forEach(c => c.checked = newState);
      }
    });

    this.sensorSelectionChanged();
  }


  toggleCheckedReadings() {
    var newState = !this.readingTypesDisplayed[0].checked;
    this.readingTypesDisplayed.forEach(reading => {
      reading.checked = newState;
    });

    this.readingTypeSelectionChanged();
  }


}
